import { memo, ReactNode, useMemo } from 'react';
import classnames from 'classnames';
import { v4 as uuid4 } from 'uuid';
import { colors } from '@/constants/tailwind';
import AnimatedCounter from '@/components/ui/AnimatedCounter';
import { roundCredits } from '../util/TrackerHelpers';
import Text from '@/components/cms/Text';

export interface IDonutColors {
  startColor: string;
  stopColor: string;
  circleColor: string;
}

export type ICreditDonutProps = {
  size?: number;
  creditsCounted: number;
  creditsEarned: number;
  creditsRequired: number;
  donutColors: IDonutColors;
  isLoading?: boolean;
  tooltip?: ReactNode;
  showCounter?: boolean;
  donutSize?: 'sm' | 'md' | 'lg' | 'miniTracker' | 'creditPlan';
  className?: string;
  donutBackgroundColor?: string;
  forceShowTooltip?: boolean;
  counterTextOverride?: string;
  counterTextOverrideClassName?: string;
  variant?: 'default' | 'ai';
};

const CreditDonut = memo(
  ({
    size = 260,
    creditsCounted,
    creditsEarned,
    creditsRequired,
    donutColors,
    isLoading,
    tooltip,
    className,
    counterTextOverride,
    counterTextOverrideClassName,
    showCounter = true,
    donutSize = 'lg',
    donutBackgroundColor = undefined,
    forceShowTooltip = false,
    variant = 'default'
  }: ICreditDonutProps) => {
    const percentCompleted =
      creditsCounted > creditsRequired ? 100 : (creditsCounted / creditsRequired) * 100;
    const strokeWidth = 30;
    const radius = size / 2 - strokeWidth / 2;
    const circumference = 2 * Math.PI * radius;
    const hasValidAiRequirement = Number.isFinite(creditsRequired) && creditsRequired > 0;
    const aiCountedRatio = hasValidAiRequirement
      ? Math.min(
          Math.max((Number.isFinite(creditsCounted) ? creditsCounted : 0) / creditsRequired, 0),
          1
        )
      : 0;
    const aiCountedPercent = aiCountedRatio * 100;
    const aiAnimationTimeOffset = aiCountedRatio * 2.2;
    const aiDarkSegmentLength = Math.max(aiCountedPercent - 60, 0);
    const aiDarkAnimationTimeOffset = ((aiDarkSegmentLength / 100) * 2.2).toFixed(3);
    const aiLeadingCircleRadius = (strokeWidth + 2) / 2;
    const aiLeadingCirclePercent = aiCountedPercent;
    const aiLineColorMix = Math.max((aiCountedPercent - 60) * 2.5, 0);
    const aiLeadingCircleColor = `color-mix(in srgb, color-mix(in srgb, ${donutColors.startColor} ${100 - aiLineColorMix}%, ${donutColors.stopColor} ${aiLineColorMix}%) 90%, black 10%)`;
    const aiProgressGradient = useMemo(
      () => ({
        background: `conic-gradient(from 0deg, ${donutColors.startColor} 0% 60%, ${donutColors.stopColor} 100%)`
      }),
      [donutColors.startColor, donutColors.stopColor]
    );
    const isComplete = percentCompleted === 100;
    const notStarted = percentCompleted === 0 || Number.isNaN(percentCompleted);
    const altText = 'percent of credits completed';
    const uniqueId = useMemo(() => uuid4(), []);

    const circlePath =
      'M130,15.8c15,0,29.9,3,43.7,8.7c13.9,5.7,26.5,14.2,37.1,24.8s19,23.2,24.8,37.1c5.7,13.9,8.7,28.7,8.7,43.7s-3,29.9-8.7,43.7c-5.7,13.9-14.2,26.5-24.8,37.1s-23.2,19-37.1,24.8c-13.9,5.7-28.7,8.7-43.7,8.7s-29.9-3-43.7-8.7c-13.9-5.7-26.4-14.2-37.1-24.8s-19-23.2-24.8-37.1c-5.7-13.9-8.7-28.7-8.7-43.7c0-15,3-29.9,8.7-43.7c5.7-13.9,14.2-26.4,24.8-37.1s23.2-19,37.1-24.8C100.1,18.7,115,15.8,130,15.8L130,15.8z';

    const strokeDashArray = [(percentCompleted * circumference) / 100, circumference].join(',');

    const earnedArray = useMemo(() => {
      if (variant === 'ai') return [];

      const length = creditsCounted > 1 ? Math.round(creditsCounted - 1) : creditsCounted;
      const array = Array.from(Array(length >= 1 ? length : 1).keys());
      array.push(creditsCounted);

      return array;
    }, [creditsCounted, variant]);
    const animationTimeOffset = (percentCompleted / 100) * 2.2;

    const progressGradient = useMemo(
      () => ({
        background: `conic-gradient(from 352deg, ${donutColors.startColor} 0%, ${donutColors.stopColor} ${percentCompleted}%)`,
        animation: `${isComplete ? `colorRotate-${uniqueId} .25s ease-in-out forwards` : 'none'}`,
        animationDelay: '1930ms'
      }),
      [donutColors.startColor, donutColors.stopColor, isComplete, percentCompleted, uniqueId]
    );
    const maskAnimation = useMemo(
      () => ({
        strokeDashoffset: `${(percentCompleted * circumference) / 100}px`,
        animation: `pathStroke-${uniqueId} ${animationTimeOffset}s 1 ease-in-out forwards`,
        animationDelay: '20ms'
      }),
      [animationTimeOffset, circumference, percentCompleted, uniqueId]
    );

    const animatedCircle = useMemo(
      () => ({
        animation: `moveAlongPath-${uniqueId} ${animationTimeOffset}s 1 ease-in-out forwards`,
        offsetPath: `path('${circlePath}')`,
        offsetAnchor: `${size}px ${size}px`
      }),
      [animationTimeOffset, size, uniqueId]
    );

    const checkmarkAnimation = useMemo(
      () => ({
        animation: `checkmarkStroke-${uniqueId} 1s 1 cubic-bezier(0, 0, 0.1, 1) forwards`,
        animationDelay: `${animationTimeOffset + 1}s`
      }),
      [animationTimeOffset, uniqueId]
    );

    const calculatedOverFlowText = useMemo(() => {
      if (donutSize != 'creditPlan' && (forceShowTooltip || creditsEarned > creditsCounted))
        return `${roundCredits(creditsEarned)} total earned`;
    }, [donutSize, forceShowTooltip, creditsEarned, creditsCounted]);

    const containerClassNames = classnames('relative shrink-0 fill-none', className, {
      'p-1': variant === 'default',
      'size-[16.25rem]': donutSize === 'lg',
      'size-[100px]': donutSize === 'md',
      'size-16': donutSize === 'sm',
      'size-[110px]': donutSize === 'miniTracker',
      'size-[156px]': donutSize === 'creditPlan'
    });

    const centerContentClassNames = classnames('w-fit', {
      'scale-[0.3846]': donutSize === 'md',
      'scale-[0.2462]': donutSize === 'sm',
      'scale-[0.4231]': donutSize === 'miniTracker',
      'scale-[0.6]': donutSize === 'creditPlan'
    });

    const loadingDonut = useMemo(() => {
      return (
        <svg
          role="img"
          viewBox={`0 0 ${size} ${size}`}
          aria-label="loading credit donut"
          className="z-50 size-full"
        >
          <path
            d={circlePath}
            className="animate-pulse bg-transparent stroke-gray-light"
            strokeWidth={strokeWidth}
          />
        </svg>
      );
    }, [size, circlePath]);

    const aiDonut = useMemo(() => {
      return (
        <svg
          role="img"
          viewBox={`0 0 ${size} ${size}`}
          aria-label={`${creditsRequired > 0 ? Math.min(Math.max(Number.isFinite(creditsCounted) ? creditsCounted : 0, 0), creditsRequired) : 0} of ${hasValidAiRequirement ? creditsRequired : 0} credits counted`}
          className="size-full overflow-visible"
        >
          <defs>
            <linearGradient
              id={`aiDonutGradient-${uniqueId}`}
              x1="0"
              y1={size}
              x2="0"
              y2="0"
              gradientUnits="userSpaceOnUse"
            >
              <stop offset="0" stopColor="#EA0029" />
              <stop offset="50%" stopColor="#9B2C98" />
              <stop offset="100%" stopColor="#040CDC" />
            </linearGradient>
            {aiDarkSegmentLength > 0 && (
              <mask id={`aiProgressMask-${uniqueId}`}>
                <path
                  d={circlePath}
                  stroke="white"
                  strokeWidth={strokeWidth + 2}
                  pathLength="100"
                  strokeDasharray="0 100"
                  strokeDashoffset="40"
                  strokeLinecap="butt"
                  style={{
                    animation: `aiDarkPathStroke-${uniqueId} ${aiDarkAnimationTimeOffset}s linear forwards`,
                    animationDelay: '1.32s'
                  }}
                />
              </mask>
            )}
          </defs>
          {/* Gray background */}
          <path
            data-segment="background"
            d={circlePath}
            stroke="#ececec"
            strokeWidth={strokeWidth}
          />
          {/* Gradient remaining fill */}
          <path
            data-segment="remaining"
            d={circlePath}
            stroke={`url(#aiDonutGradient-${uniqueId})`}
            strokeWidth={strokeWidth}
            pathLength="100"
            strokeDasharray="100 100"
            strokeDashoffset="100"
            strokeLinecap="round"
            shapeRendering="geometricPrecision"
            style={{
              animation: `aiGradientStroke-${uniqueId} 2.2s linear forwards`
            }}
          />
          {aiCountedPercent > 0 && (
            <path
              data-segment="counted"
              d={circlePath}
              stroke={donutColors.startColor}
              strokeWidth={strokeWidth + 2}
              pathLength="100"
              strokeDasharray={`${aiCountedPercent} 100`}
              strokeDashoffset={aiCountedPercent}
              strokeLinecap="round"
              style={{
                animation: `aiPathStroke-${uniqueId} ${aiAnimationTimeOffset}s linear forwards`
              }}
            />
          )}
          {aiDarkSegmentLength > 0 && (
            <foreignObject
              data-segment="counted-gradient"
              width={size}
              height={size}
              mask={`url(#aiProgressMask-${uniqueId})`}
            >
              <div className="size-full rounded-full" style={aiProgressGradient} />
            </foreignObject>
          )}
          {aiCountedPercent > 0 && (
            <>
              <circle
                data-segment="leading-circle"
                className="drop-shadow-[0_0_1px_rgba(0,0,0,0.5)]"
                cx={size}
                cy={size}
                r={aiLeadingCircleRadius}
                fill={donutColors.circleColor}
                fillOpacity={0.8}
                style={{
                  fill: aiLeadingCircleColor,
                  animation: `aiMoveAlongPath-${uniqueId} ${aiAnimationTimeOffset}s linear forwards`,
                  offsetPath: `path('${circlePath}')`,
                  offsetAnchor: `${size}px ${size}px`
                }}
              />
              <circle
                cx={size}
                cy={size}
                r={3}
                fill="white"
                style={{
                  animation: `aiMoveAlongPath-${uniqueId} ${aiAnimationTimeOffset}s linear forwards`,
                  offsetPath: `path('${circlePath}')`,
                  offsetAnchor: `${size}px ${size}px`
                }}
              />
            </>
          )}
          <style>
            {`
              @keyframes aiPathStroke-${uniqueId} {
                to { stroke-dashoffset: 0; }
              }
              @keyframes aiDarkPathStroke-${uniqueId} {
                to { stroke-dasharray: ${aiDarkSegmentLength} ${100 - aiDarkSegmentLength}; }
              }
              @keyframes aiMoveAlongPath-${uniqueId} {
                to { offset-distance: ${aiLeadingCirclePercent}%; }
              }
              @keyframes aiGradientStroke-${uniqueId} {
                to { stroke-dashoffset: 0; }
              }
              @keyframes colorRotate-${uniqueId} {
                0% { background: conic-gradient(from 352deg, ${donutColors.startColor} 0% 60%, ${donutColors.stopColor} 100%); }
                100% { background: conic-gradient(from 357deg, ${donutColors.startColor} 0% 60%, ${donutColors.stopColor} 100%); }
              }
            `}
          </style>
        </svg>
      );
    }, [
      aiAnimationTimeOffset,
      aiCountedPercent,
      aiDarkAnimationTimeOffset,
      aiDarkSegmentLength,
      aiLeadingCircleColor,
      aiLeadingCirclePercent,
      aiLeadingCircleRadius,
      aiProgressGradient,
      creditsCounted,
      creditsRequired,
      donutColors.circleColor,
      donutColors.startColor,
      donutColors.stopColor,
      hasValidAiRequirement,
      size,
      uniqueId
    ]);

    const trackerDonut = useMemo(() => {
      return (
        <>
          {donutSize !== 'miniTracker' && (
            <svg
              role="img"
              viewBox={`0 0 ${size} ${size}`}
              style={{ zIndex: 60 }}
              aria-label={`${percentCompleted.toFixed(0).toString()} ${altText}`}
              className="absolute inset-0 size-full"
            >
              <defs>
                <mask id={`progressPathMask2-${uniqueId}`}>
                  <rect x="0" y="0" width="100%" height="100%" fill="white" />
                  <path
                    style={maskAnimation}
                    strokeWidth={strokeWidth}
                    strokeDasharray={strokeDashArray}
                    strokeLinecap="round"
                    shapeRendering="geometricPrecision"
                    d={circlePath}
                    stroke="black"
                  />
                  <circle
                    style={animatedCircle}
                    cx={size}
                    cy={size}
                    r={15}
                    fill="black"
                    fillOpacity={notStarted ? '.1' : '1'}
                  ></circle>
                </mask>
              </defs>
              <path
                d={circlePath}
                stroke="#F5F5F5"
                strokeWidth={strokeWidth}
                mask={`url(#progressPathMask2-${uniqueId})`}
              />
            </svg>
          )}
          <svg
            role="img"
            viewBox={`0 0 ${size} ${size}`}
            aria-label={`${percentCompleted.toFixed(0).toString()} ${altText}`}
            className="absolute inset-0 z-50 size-full"
          >
            <defs>
              {donutBackgroundColor && (
                <mask id={`progressPathMask-${uniqueId}`}>
                  <path
                    style={maskAnimation}
                    strokeWidth={strokeWidth}
                    strokeDasharray={strokeDashArray}
                    strokeLinecap="round"
                    shapeRendering="geometricPrecision"
                    d={circlePath}
                    stroke="white"
                  />
                  <circle
                    style={animatedCircle}
                    cx={size}
                    cy={size}
                    r={15}
                    fill="white"
                    fillOpacity={notStarted ? '.1' : '1'}
                  ></circle>
                </mask>
              )}
              <mask
                id={`progressStrokeMask-${uniqueId}`}
                maskUnits="userSpaceOnUse"
                x="0"
                y="0"
                width={size}
                height={size}
              >
                <path
                  style={maskAnimation}
                  strokeWidth={strokeWidth}
                  strokeDasharray={strokeDashArray}
                  strokeLinecap="round"
                  shapeRendering="geometricPrecision"
                  d={circlePath}
                  stroke="white"
                />
              </mask>
            </defs>
            <g mask={donutBackgroundColor ? `url(#progressPathMask-${uniqueId})` : ''}>
              {!notStarted && (
                <foreignObject
                  x="0"
                  y="0"
                  width={size}
                  height={size}
                  mask={`url(#progressStrokeMask-${uniqueId})`}
                >
                  <div style={progressGradient} className="size-full rounded-full" />
                </foreignObject>
              )}
              <path d={circlePath} strokeWidth={strokeWidth} />
              <circle
                style={animatedCircle}
                cx={size}
                cy={size}
                r={15}
                fill={notStarted ? '#000000' : donutColors.circleColor}
                fillOpacity={notStarted ? '.1' : '1'}
              ></circle>
              <circle style={animatedCircle} cx={size} cy={size} r={3} fill="white"></circle>
            </g>
          </svg>
          {donutBackgroundColor && (
            <svg
              role="img"
              viewBox={`0 0 ${size} ${size}`}
              className="absolute inset-0 z-40 size-full"
            >
              <path
                d={circlePath}
                style={{ stroke: `#${donutBackgroundColor}` }}
                strokeWidth={strokeWidth}
              />
            </svg>
          )}
          <div className="absolute inset-0 z-[61] flex items-center justify-center">
            <div className={centerContentClassNames}>
              {isComplete && (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="m-auto flex flex-col items-center justify-center"
                  viewBox="0 0 37 26"
                  width="37"
                  height="26"
                  fill="none"
                >
                  <path
                    style={checkmarkAnimation}
                    stroke={colors.serpentine.DEFAULT}
                    strokeLinecap="square"
                    strokeWidth="5"
                    strokeDasharray="36,100"
                    strokeDashoffset="36"
                    d="M5.345 12.797 13.37 21 31.656 5"
                  />
                </svg>
              )}
              {counterTextOverride ? (
                <Text property="span" className={counterTextOverrideClassName ?? 'text-2-bold'}>
                  {counterTextOverride}
                </Text>
              ) : (
                showCounter && (
                  <AnimatedCounter
                    containerClassName={classnames(
                      'flex h-8 w-full flex-col items-center justify-center overflow-hidden',
                      donutSize === 'creditPlan' ? 'mt-4' : 'mt-2'
                    )}
                    total={creditsRequired}
                    numbers={earnedArray}
                    description={
                      !isComplete && donutSize != 'creditPlan' ? 'Counted / Required' : ''
                    }
                    overFlowText={calculatedOverFlowText}
                    hexColor={
                      !isComplete && donutColors.stopColor === '#CF4B00'
                        ? donutColors.stopColor
                        : ''
                    }
                    animationOffset={animationTimeOffset}
                    forceShowTooltip={forceShowTooltip}
                    tooltip={tooltip}
                  />
                )
              )}
            </div>
          </div>
          <style>
            {`
          @keyframes pathStroke-${uniqueId} {
            0% {  stroke-dashoffset: ${((percentCompleted * circumference) / 100 - 1).toFixed(0)};}
            100% { stroke-dashoffset: 0; }
          }
          @keyframes checkmarkStroke-${uniqueId} {
            0% {  stroke-dashoffset: 40; }
            100% { stroke-dashoffset: 0; }
          }
          @keyframes colorRotate-${uniqueId} {
            0% { background: conic-gradient(from 352deg, ${donutColors.startColor} 0%, ${donutColors.stopColor} ${percentCompleted}%);}
            100% { background: conic-gradient(from 357deg, ${donutColors.startColor} 0%, ${donutColors.stopColor} ${percentCompleted}%);}
          }
          @keyframes moveAlongPath-${uniqueId} {
            0% { offset-distance: 0%; }
            100% {
              offset-distance: ${percentCompleted < 50 || isComplete ? percentCompleted : (percentCompleted + 1).toFixed(0)}%;
            }
          }
        `}
          </style>
        </>
      );
    }, [
      animationTimeOffset,
      animatedCircle,
      calculatedOverFlowText,
      centerContentClassNames,
      checkmarkAnimation,
      circlePath,
      circumference,
      counterTextOverride,
      counterTextOverrideClassName,
      creditsRequired,
      donutBackgroundColor,
      donutColors.circleColor,
      donutColors.startColor,
      donutColors.stopColor,
      donutSize,
      earnedArray,
      forceShowTooltip,
      isComplete,
      maskAnimation,
      notStarted,
      percentCompleted,
      progressGradient,
      showCounter,
      size,
      strokeDashArray,
      tooltip,
      uniqueId
    ]);

    const renderedDonut = useMemo(
      () => (isLoading ? loadingDonut : variant === 'ai' ? aiDonut : trackerDonut),
      [aiDonut, isLoading, loadingDonut, trackerDonut, variant]
    );

    const dataPercentCompleted = useMemo(
      () => (variant === 'ai' ? aiCountedRatio * 100 : percentCompleted).toFixed(0),
      [aiCountedRatio, percentCompleted, variant]
    );

    return (
      <div
        key={creditsCounted}
        className={containerClassNames}
        data-component="CreditDonut"
        data-percentcompleted={dataPercentCompleted}
      >
        {renderedDonut}
      </div>
    );
  }
);

CreditDonut.displayName = 'CreditDonut';
export default CreditDonut;
